<template>
  <transition
    appear
    name="p-drawer"
  >
    <div
      v-if="shouldShowDrawer"
      :class="drawerClasses"
      data-test="navbar-customer-tablet-sidenavbar"
      role="complementary"
    >
      <a
        class="flex"
        :class="[isDesktopDrawerExpanded ? 'justify-end' : 'justify-center']"
        @click="toggleDesktopDrawer"
      >
        <span class="bg-gray-100 rounded-full text-gray-500 size-12 md:size-8 flex items-center justify-center">
          <FontAwesomeIcon
            :icon="isDesktopDrawerExpanded ? 'arrow-left-to-line' : 'arrow-right-to-line'"
            :size="mq.isMobile ? 'lg' : 'md'"
          />
        </span>
      </a>

      <div class="flex justify-center">
        <avatar
          v-if="!isDesktopDrawerExpanded"
          class="flex items-center justify-center"
          :format="current_tenant?.attributes?.picture_logo_format"
          size="40"
          :src="current_tenant?.attributes?.picture_logo_url_s"
          :username="current_tenant?.attributes?.name"
        />
        <div
          v-else
          class="flex items-center justify-center"
        >
          <img
            alt="Logo"
            class="max-h-full max-w-full object-contain"
            :src="current_tenant?.attributes?.picture_logo_url_s"
          />
        </div>
      </div>

      <div class="flex justify-center w-full items-center h-full">
        <Dropdown
          v-if="$_.values(projects).length > 1 && isDesktopDrawerExpanded"
          class="w-full"
          :modelValue="current_project?.id"
          :options="$_.values(projects)"
          optionLabel="attributes.name"
          optionValue="id"
          @update:modelValue="initializeProject"
        />

        <div
          v-else-if="isDesktopDrawerExpanded"
          class="flex items-center justify-center text-center font-bold text-gray-500 line-clamp-2"
        >
          {{ $truncate(current_project?.attributes?.name, 45) }}
        </div>
      </div>

      <div
        class="flex flex-col gap-2 w-full h-full overflow-y-auto"
        :class="{ 'items-center': !isDesktopDrawerExpanded, 'items-start': isDesktopDrawerExpanded }"
      >
        <div class="flex flex-col gap-2 flex-1 w-full">
          <NavbarItem
            v-for="(nav_item, idx) in routesActive"
            :key="idx"
            :hide_title="!isDesktopDrawerExpanded"
            :item="nav_item"
            :params="{ project_id: current_project?.id }"
            @update:modelValue="toggleDesktopDrawer"
          />
        </div>
      </div>

      <ButtonPrimary
        v-if="app.$flipper.enabled($FT.MOBILE_APP_CUSTOMER) && !mq.isDesktop"
        class="progressier-install-button font-semibold"
        data-icons="false"
        :data-install="$t('profile.actions.installApp')"
        :data-installed="$t('profile.actions.launchApp')"
      >
      </ButtonPrimary>

      <ButtonPrimary
        v-else-if="app.$flipper.enabled($FT.MOBILE_APP_CUSTOMER) && mq.isDesktop && isDesktopDrawerExpanded"
        @onClick="show_install_pwa_desktop_dialog = true"
      >
        {{ $t('profile.actions.installApp') }}
      </ButtonPrimary>

      <PrimeDialog
        v-model:visible="show_install_pwa_desktop_dialog"
        modal
        :style="{ width: '55rem' }"
        @close="show_install_pwa_desktop_dialog = false"
      >
        <template #header>
          <div class="flex items-center gap-3">
            <span class="bg-blue-100 rounded-full size-12 flex items-center justify-center">
              <FontAwesomeIcon
                :icon="['far', 'mobile']"
                class="text-blue-600"
                size="lg"
              />
            </span>
            <h2 class="text-2xl font-bold">{{ $t('profile.labels.installMobileApp') }}</h2>
          </div>
        </template>

        <div class="space-y-6 py-4">
          <!-- Step 1 -->
          <div class="flex gap-4">
            <div class="flex-shrink-0">
              <span
                class="bg-teal-100 text-teal-700 rounded-full size-12 flex items-center justify-center font-semibold text-lg"
                >1</span
              >
            </div>
            <div class="flex-1">
              <h3 class="text-lg font-semibold mb-2 flex items-center gap-2">
                <FontAwesomeIcon :icon="['far', 'arrow-right-to-bracket']" />
                {{ $t('profile.labels.logInMobileDevice') }}
              </h3>
              <p class="text-gray-600 mb-2">
                {{ $t('profile.labels.openMobileBrowser') }}
                <span class="inline-block bg-gray-100 px-2 py-1 rounded font-mono text-sm mx-1">
                  {{ current_tenant?.attributes?.subdomain }}.ziggu.app
                </span>
                {{ $t('profile.labels.toLogInCredentials') }}
              </p>
            </div>
          </div>

          <!-- Step 2 -->
          <div class="flex gap-4">
            <div class="flex-shrink-0">
              <span
                class="bg-teal-100 text-teal-700 rounded-full size-12 flex items-center justify-center font-semibold text-lg"
                >2</span
              >
            </div>
            <div class="flex-1">
              <h3 class="text-lg font-semibold mb-2 flex items-center gap-2">
                <FontAwesomeIcon :icon="['far', 'qrcode']" />
                {{ $t('profile.labels.scanQRCode') }}
              </h3>

              <p class="text-gray-600 mb-1">
                {{ $t('profile.labels.onceLoggedInScan') }}
              </p>
              <ButtonPrimary
                class="progressier-install-button font-semibold w-full md:w-auto"
                data-icons="false"
                :data-install="$t('profile.actions.showQRCode')"
                :data-installed="$t('profile.actions.launchApp')"
              >
              </ButtonPrimary>
            </div>
          </div>

          <!-- Step 3 -->
          <div class="flex gap-4">
            <div class="flex-shrink-0">
              <span
                class="bg-teal-100 text-teal-700 rounded-full size-12 flex items-center justify-center font-semibold text-lg"
                >3</span
              >
            </div>
            <div class="flex-1">
              <h3 class="text-lg font-semibold mb-2 flex items-center gap-2">
                <FontAwesomeIcon :icon="['far', 'check']" />
                {{ $t('profile.labels.addToHomeScreen') }}
              </h3>
              <p class="text-gray-600">
                {{ $t('profile.labels.followDevicePrompts') }}
              </p>
            </div>
          </div>
        </div>

        <template #footer>
          <div class="flex justify-center w-full"></div>
        </template>
      </PrimeDialog>

      <ProfileDropdown
        :is_mobile="mq.isMobile"
        :show_name="isDesktopDrawerExpanded"
        @close-sidebar="emit('update:modelValue', false)"
      />
    </div>
  </transition>
</template>

<script setup>
import { computed, getCurrentInstance, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useMqService } from '@/plugins/mq';
import ProfileDropdown from '@/app/shared_components/navbar/TheProfileDropdown.vue';
import { NAVBAR_ITEMS_CUSTOMER } from '@/app/data/navbar_constants';
import { ROUTES_CUSTOMERS } from '@/app/data/route_constants';
import NavbarItem from '@/app/shared_components/navbar/NavbarItem.vue';

const { proxy: app } = getCurrentInstance();

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
});

const mq = useMqService();
const store = useStore();
const router = useRouter();

const emit = defineEmits(['update:modelValue']);

const isDesktopDrawerExpanded = ref(true);
const show_install_pwa_desktop_dialog = ref(false);

const current_client = computed(() => store.getters.current_client);
const current_project = computed(() => store.getters.current_project);
const projects = computed(() => store.getters.projects);
const current_tenant = computed(() => store.getters.current_tenant);

const isRouteActive = (route_item) => {
  if (route_item.flipper) {
    return app.$flipper.enabled(route_item.flipper);
  }
  return true;
};

const routesActive = computed(() => _.filter(NAVBAR_ITEMS_CUSTOMER, isRouteActive));

const shouldShowDrawer = computed(() => (mq.isMobile ? props.modelValue : true));

const drawerClasses = computed(() => {
  const baseClasses =
    'bg-white grid grid-cols-1 grid-rows-[auto_70px_80px_2fr_auto] shadow-right z-40 shadow-lg h-full mb-0 shrink-0';

  if (mq.isMobile) {
    return `fixed top-0 left-0 w-full p-2 ${baseClasses}`;
  }
  const widthClass = isDesktopDrawerExpanded.value ? 'w-[250px] px-4' : 'w-[60px] px-2';
  return `lg:static top-0 left-0 ${widthClass} py-3 ${baseClasses} md:rounded-r-3xl`;
});

const toggleDesktopDrawer = () => {
  if (!mq.isMobile) {
    isDesktopDrawerExpanded.value = !isDesktopDrawerExpanded.value;
  } else {
    emit('update:modelValue', !props.modelValue);
  }
};

const initializeProject = (project_id) => {
  router.push({
    name: ROUTES_CUSTOMERS.DASHBOARD,
    params: { project_id, client_id: current_client.value.id },
    query: {
      tab: 'dashboard',
    },
  });
};
</script>

<style scoped>
.p-drawer-enter-active,
.p-drawer-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.p-drawer-enter-from {
  transform: translateX(-100%);
}

.p-drawer-leave-to {
  transform: translateX(-100%);
}

.p-drawer-enter-to,
.p-drawer-leave-from {
  transform: translateX(0);
}

.transition-opacity {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@media (min-width: 1024px) {
  .lg\:static {
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
}
</style>
